import { CommonModule } from "@angular/common";
import { Component, OnInit, AfterViewInit, ViewChild, VERSION, ElementRef, ChangeDetectorRef, Renderer2 } from '@angular/core';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { UtilityService } from 'src/app/services/utility.service';
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { FormControl, FormGroup, NgForm, FormBuilder } from "@angular/forms";
import { DrupalRESTService } from "src/app/services/drupal-rest.service";
import { ActivatedRoute, Event, Params, Router } from "@angular/router";
import { saveAs } from 'file-saver';
import moment from "moment";
import { ViewTableComponent } from "../../base/view-table/view-table.component";
import { AuthService } from 'src/app/services/auth.service';
import { PrintService } from 'src/app/services/print.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer } from '@angular/platform-browser';
import { RefreshService } from 'src/app/services/refresh.service';
import { DialogService } from 'src/app/services/dialog.service';
import { EntityRESTService } from 'src/app/services/entity-rest.service';
import { FieldsService } from 'src/app/services/fields.service';
import { TaxonomyService } from 'src/app/services/taxonomy.service';
import { AuthRESTService } from 'src/app/services/auth-rest.service';
import { MenuRESTService } from 'src/app/services/menu-rest.service';
import { RouteEventsService } from 'src/app/route-events.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { ConfigPagesService } from 'src/app/services/config-pages.service';
import { PackagesService } from 'src/app/services/packages.service';
import { UserService } from 'src/app/services/user.service';
import { StudioConfigService } from 'src/app/services/studio-config.service';
import { ComponentRefreshService } from 'src/app/services/component-refresh.service';
import { TranslateService } from '@ngx-translate/core';
import { WeekYearChangeEvent } from '../../shared/week-year-selector/week-year-selector.component';
import { CurrencyService } from '../../../services/currency.service';
import { LoggerService } from '../../../services/logger.service';

@Component({
  selector: 'app-royalty-report',
  templateUrl: './royalty-report.component.html',
  styleUrls: ['./royalty-report.component.css']
})
export class RoyaltyReportComponent extends ViewTableComponent implements OnInit {

  override displayedColumns: string[] = [
    'receipt_number',
    'date_paid',
    'student_name',
    'executive',
    'instructor',
    'sale_code',
    'package_name',
    'lesson_count',
    'total_price',
    'cash',
    'field_tax_amount',
    'miscellaneous_services',
    'subject_to_royalty',
    'sundry',
    'total_receipts'
  ];

  currentReport: string = 'studioBusiness';
  week: string;
  year: string;
  royaltyPercentage;
  royaltyDataRefunds;
  groupedDataByDay: { [key: string]: any[] } = {};
  dailySubtotals: { [key: string]: any } = {};
  isDataReady: boolean = false;

  // Track by function for *ngFor to help with change detection
  trackByDate(index: number, dateKey: string): string {
    return dateKey;
  }

  trackByRowId(index: number, row: any): string {
    return row.receipt_number || index.toString();
  }

  // Safe getters to prevent template errors
  getSafeGroupedDataByDay(): { [key: string]: any[] } {
    return this.groupedDataByDay || {};
  }

  getSafeDailySubtotals(): { [key: string]: any } {
    return this.dailySubtotals || {};
  }

  getSafeSubtotalForDate(dateKey: string): any {
    try {
      if (!dateKey) {
        LoggerService.warn('getSafeSubtotalForDate called with empty dateKey');
        return this.getDefaultSubtotal();
      }

      const subtotals = this.getSafeDailySubtotals();
      if (!subtotals || typeof subtotals !== 'object') {
        LoggerService.warn('getSafeDailySubtotals returned invalid data:', subtotals);
        return this.getDefaultSubtotal();
      }

      const result = subtotals[dateKey];
      if (!result || typeof result !== 'object') {
        LoggerService.warn(`No subtotal found for dateKey: ${dateKey}, returning default`);
        return this.getDefaultSubtotal();
      }

      return result;
    } catch (error) {
      LoggerService.error('Error in getSafeSubtotalForDate:', error);
      return this.getDefaultSubtotal();
    }
  }

  constructor(
    public override _refreshService: RefreshService,
    private _printService: PrintService,
    public override _dialogService: DialogService,
    public override _entityRESTService: EntityRESTService,
    public override _utilityService: UtilityService,
    public override _fieldsService: FieldsService,
    public override _drupalRESTService: DrupalRESTService,
    public override _taxonomyService: TaxonomyService,
    public override _router: Router,
    public override _activatedRoute: ActivatedRoute,
    public override _authRESTService: AuthRESTService,
    public override _authService: AuthService,
    public override _menuRESTService: MenuRESTService,
    public override _formBuilder: FormBuilder,
    public override _routeEventsService: RouteEventsService,
    public override _permissionsService: PermissionsService,
    public override _renderer2: Renderer2,
    public override _configPages: ConfigPagesService,
    public override _packagesService: PackagesService,
    public override _snackBar: MatSnackBar,
    public override _userRESTService: UserService,
    public override _el: ElementRef,
    public override _changeDetectorRef: ChangeDetectorRef,
    public override _sanitizer: DomSanitizer,
    public override _studioConfigService: StudioConfigService,
    public override _componentRefreshService: ComponentRefreshService,
    public override _translateService: TranslateService,
    private currencyService: CurrencyService
  ) {
    super(
      _refreshService,
      _dialogService,
      _entityRESTService,
      _utilityService,
      _fieldsService,
      _drupalRESTService,
      _taxonomyService,
      _router,
      _activatedRoute,
      _authRESTService,
      _authService,
      _menuRESTService,
      _formBuilder,
      _routeEventsService,
      _permissionsService,
      _renderer2,
      _configPages,
      _packagesService,
      _snackBar,
      _userRESTService,
      _el,
      _changeDetectorRef,
      _sanitizer,
      _studioConfigService,
      _componentRefreshService,
      _translateService
    );
  }

  private getDefaultSubtotal(): any {
    return {
      cash: 0,
      tax: 0,
      miscellaneous_services: 0,
      subject_to_royalty: 0,
      sundry: 0,
      total_receipts: 0,
      total_price: 0,
      lesson_count: 0
    };
  }

  // Get the current studio name from AuthService
  get currentStudio(): string {
    return this._authService.selectedStudio?.name ||
           this._authService.selectedStudio?.key ||
           'Unknown Studio';
  }

  // Get tooltip message for disabled send/update buttons
  get disabledButtonTooltip(): string {
    if (!this.isDisabled) {
      return '';
    }

    if (this.week && this.year) {
      let selectedDate = moment().isoWeekYear(+this.year).isoWeek(+this.week);
      let weekEnd = selectedDate.clone().endOf('isoWeek').startOf('day').add(1, 'day'); // Monday after the week ends
      let now = moment();

      // Calculate days until Monday (when report is actually available)
      let daysUntilReady = Math.ceil(weekEnd.diff(now, 'days', true));

      if (daysUntilReady < 0) {
        return ''; // Should not happen if isDisabled logic is correct
      } else if (daysUntilReady === 1) {
        return 'Ready tomorrow';
      } else {
        return `Ready in ${daysUntilReady} days`;
      }
    }

    return 'Ready after the week ends';
  }

  onYearChange(event: any) {
    this.year = event.value;
    LoggerService.log('year', this.year)
    this.WeeksForYearData = this._fieldsService.WeeksForYearData(Number.parseInt(this.year));
  }

  onWeekYearChange(event: WeekYearChangeEvent): void {
    this.week = event.week;
    this.year = event.year;
    this.getData([
      { parameter: 'week', value: this.week },
      { parameter: 'year', value: this.year },
    ]);
  }

  override ngOnInit(): void {
    // Initialize properties to ensure they're never undefined
    this.groupedDataByDay = this.groupedDataByDay || {};
    this.dailySubtotals = this.dailySubtotals || {};

    // Pull royalty percentage.
    this._fieldsService.getRoyaltyPercentageConfig().subscribe(data => {
      this.setRoyaltyPercentageBasedOnDate(data);
    });
  }

  override ngAfterContentInit(): void {
    this._activatedRoute.queryParams.subscribe(data => {
      // LoggerService.log('subscribed data', data)
      this.ShowProgressBar = false;

      if (data['year']) {
        this.year = data['year']
      } else {
        this.year = this._fieldsService.getCorrectYearForWeek(new Date()).toString();
      }
      if (data['week']) {
        this.week = data['week']
      } else {
        this.week = this._fieldsService.getCurrentWeek().toString();
      }

      // Update weeks dropdown data to match the year
      this.WeeksForYearData = this._fieldsService.WeeksForYearData(Number.parseInt(this.year));

      this.getData([
        { parameter: 'week', value: this.week},
        { parameter: 'year', value: this.year},
      ]);
    })
  }

  override getData(params: { parameter: string; value: string; }[]) {
    // Create a moment object for the selected week/year
    let selectedDate = moment().isoWeekYear(+this.year).isoWeek(+this.week);
    // Get end of selected week (Sunday)
    let weekEnd = selectedDate.clone().endOf('isoWeek');
    // Create current date for comparison
    let currentDate = moment();

    // Compare the dates - disable if current date hasn't passed Sunday of the selected week
    this.isDisabled = !weekEnd.isBefore(currentDate);

    this.clearFormTableData();

    // Show Progress Bar (Loading indicator)
    this.ShowProgressBar = true;
    this.queryParams = params;

    // Add sorting paramaters, if available.
    if (this.sortParams !== undefined) {
      this.queryParams.push(this.sortParams)
    }

    // Load the new student list with the filtering parameters.
    this._utilityService.getRoyaltyReport(
      this.queryParams
    )
      .subscribe((data) => {
        // ShowProgressBar
        this.ShowProgressBar = false;
        // LoggerService.log(data['#royalty_content_table']?.staff_data_4_system['#rows']);

        let rows = data['#royalty_content_table']?.staff_data_4_system['#rows'];

        // No rows? Clear existing data and return...
        if (!rows || rows.length === 0) {
          this.isDataReady = false;
          this.groupedDataByDay = {};
          this.dailySubtotals = {};
          this.royaltyDataRefunds = [];
          this.dataSource = new MatTableDataSource([]);
          this.data = data;
          this.updatePaginationConfig([]);
          return;
        }

        // Check if any of the data has a non-zero or non-empty field_tax_collected
        const hasTaxData = rows.some(
          (item: any) =>
            item.field_tax_collected !== '' &&
            item.field_tax_collected !== '0' &&
            item.field_tax_collected !== '0.00' &&
            item.field_tax_collected != null
        );

        if (hasTaxData) {
          // If tax data exists, include the "Tax" column
          if (!this.displayedColumns.includes('field_tax_amount')) {
            this.displayedColumns.push('field_tax_amount');
          }
        } else {
          // If no tax data exists, remove the "Tax" column
          const taxColumnIndex = this.displayedColumns.indexOf('field_tax_amount');
          if (taxColumnIndex !== -1) {
            this.displayedColumns.splice(taxColumnIndex, 1);
          }
        }

        // Modify data to work in Angular.
        rows.forEach((element, index) => {
          if (element.student_name) {
            rows[index]['student_name'] = element.student_name.replace(/<[^>]*>?/gm, '');
          }
        });

        // Extract the refunds from the rows to be displayed seperately.
        this.filterRefunds(rows);

        // Group data by day and calculate subtotals
        this.groupDataByDay(rows);

        this.data = data;
        this.dataSource = new MatTableDataSource(rows);

        // Data is ready for template rendering
        this.isDataReady = true;

        // Recalculate royalty percentage based on the new data
        this._fieldsService.getRoyaltyPercentageConfig().subscribe(royaltyData => {
          this.setRoyaltyPercentageBasedOnDate(royaltyData);
        });

        // Pagination configuration.
        this.updatePaginationConfig(rows);
      },
        error => this.handleError(error)
      )
  }

    override clearFormTableData() {
    // Call parent method to clear basic data
    super.clearFormTableData();

    // Clear our additional grouped data
    this.isDataReady = false;
    this.groupedDataByDay = {};
    this.dailySubtotals = {};
    this.royaltyDataRefunds = [];
  }

  filterRefunds(rows) {
    this.royaltyDataRefunds = rows.filter(object => {
      // let isRefund = (+object.total_receipts.replace('$', '') < 0);
      let isRefund = (+object.total_receipts < 0);

      return (isRefund);
    });

    // LoggerService.log('refunds', this.royaltyDataRefunds);
  }

  calcRoyaltyPercentage(amount) {
    amount = this._filterDigits(amount+'');
    const result = amount * (this.royaltyPercentage / 100);
    return this.roundNumber(result, 2);
  }

  setRoyaltyPercentageBasedOnDate(data) {
    // Extract start and end dates of the specified week.
    const startDate = moment().year(+this.year).isoWeek(+this.week).startOf('isoWeek').utc().startOf('day').toDate();
    const endDate = moment().year(+this.year).isoWeek(+this.week).endOf('isoWeek').utc().endOf('day').toDate();

    let foundMatchingPeriod = false;
    let lowestPercentage = Infinity;

    // Check if 'royalty_periods' exists and has data.
    if (data.royalty_periods && data.royalty_periods.length > 0) {
      for (const period of data.royalty_periods) {
        // Extract period start and end dates and check if they exist.
        const periodStartDate = moment(period.start_date).utc().startOf('day').toDate();
        const periodEndDate = moment(period.end_date).utc().endOf('day').toDate();

        // If start and end dates exist, check for matching period.
        if (periodStartDate && periodEndDate && startDate >= periodStartDate && endDate <= periodEndDate) {
          let percentage = parseFloat(period.royalty_percentage);
          if (percentage < lowestPercentage) {
            lowestPercentage = percentage;
            this.royaltyPercentage = percentage;
            foundMatchingPeriod = true;
          }
        }
      }
    }
    // If no matching period is found, set the default royalty percentage
    if (!foundMatchingPeriod && data.default_royalty_percentage) {
      LoggerService.log('no matching period found, using default royalty percentage', data.default_royalty_percentage)
      this.royaltyPercentage = parseFloat(data.default_royalty_percentage);
    }
  }

  combineData(firstData: any[], secondData: any[]): any[] {
    const transformedSecondData = this.transformSecondDataset(secondData);
    return [...firstData, ...transformedSecondData];
  }
  transformSecondDataset(secondData: any[]): any[] {
    return secondData.map(item => {
      return {
        receipt_number: '',  // Placeholder
        date_paid: '',       // Placeholder
        student_name: '',    // Placeholder
        executive: '',       // Placeholder
        field_executive_display: '',  // Placeholder
        field_instructor_display: '', // Placeholder
        sale_code: '',       // Placeholder
        package_name: '',    // Placeholder
        lesson_count: '',    // Placeholder
        total_price: '',     // Placeholder
        cash: item.total?.cash || item.total_refunded?.cash || item.total_include_refunded?.cash || item.royalty_percentage?.cash,
        miscellaneous_services: item.total?.miscellaneous_services || item.total_refunded?.miscellaneous_services || item.total_include_refunded?.miscellaneous_services || item.royalty_percentage?.miscellaneous_services,
        subject_to_royalty: item.total?.subject_to_royalty || item.total_refunded?.subject_to_royalty || item.total_include_refunded?.subject_to_royalty || item.royalty_percentage?.subject_to_royalty,
        sundry: item.total?.sundry || item.total_refunded?.sundry || item.total_include_refunded?.sundry,
        total_receipts: item.total?.total_receipts || item.total_refunded?.total_receipts || item.total_include_refunded?.total_receipts || item.royalty_percentage?.total_receipts
      };
    });
  }

  // Helper method to round numbers to 2 decimal places to fix floating point precision issues
  roundNumber(value: number, decimals: number = 2): number {
    return Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals);
  }

  // Format money values: whole numbers stay as-is ($400), fractional always show 2 decimals ($9.10, not $9.1)
  formatMoney(value: any): string {
    const num = +(value || 0);
    const rounded = this.roundNumber(num, 2);
    if (rounded % 1 === 0) {
      return rounded.toString();
    }
    return rounded.toFixed(2);
  }

  // Template helper method for currency calculations
  calculateTotal(...values: number[]): number {
    const sum = values.reduce((acc, val) => acc + (val || 0), 0);
    return this.roundNumber(sum, 2);
  }

  groupDataByDay(rows: any[]) {
    this.groupedDataByDay = {};
    this.dailySubtotals = {};

    // Group rows by date
    rows.forEach(row => {
      const dateKey = row.date_paid || 'No Date';

      if (!this.groupedDataByDay[dateKey]) {
        this.groupedDataByDay[dateKey] = [];
        this.dailySubtotals[dateKey] = {
          cash: 0,
          tax: 0,
          miscellaneous_services: 0,
          subject_to_royalty: 0,
          sundry: 0,
          total_receipts: 0,
          total_price: 0,
          lesson_count: 0
        };
      }

      this.groupedDataByDay[dateKey].push(row);

      // Calculate subtotals for the day
      this.dailySubtotals[dateKey].cash += +(row.cash || 0);
      this.dailySubtotals[dateKey].tax += +(row.field_tax_collected || 0);
      this.dailySubtotals[dateKey].miscellaneous_services += +(row.miscellaneous_services || 0);
      this.dailySubtotals[dateKey].subject_to_royalty += +(row.subject_to_royalty || 0);
      this.dailySubtotals[dateKey].sundry += +(row.sundry || 0);
      this.dailySubtotals[dateKey].total_receipts += +(row.total_receipts || 0);
      this.dailySubtotals[dateKey].total_price += +(row.total_price || 0);
      this.dailySubtotals[dateKey].lesson_count += +(row.lesson_count || 0);
    });

    // Round all subtotals to 2 decimal places
    Object.keys(this.dailySubtotals).forEach(dateKey => {
      Object.keys(this.dailySubtotals[dateKey]).forEach(key => {
        if (key !== 'lesson_count') {
          this.dailySubtotals[dateKey][key] = this.roundNumber(this.dailySubtotals[dateKey][key], 2);
        }
      });
    });
  }

  // Get sorted date keys for consistent display order
  getSortedDateKeys(): string[] {
    // Only return keys when data is ready and both objects exist
    if (!this.isDataReady ||
        !this.groupedDataByDay ||
        typeof this.groupedDataByDay !== 'object' ||
        !this.dailySubtotals ||
        typeof this.dailySubtotals !== 'object') {
      return [];
    }

    // Only return keys that exist in BOTH objects
    const groupedKeys = Object.keys(this.groupedDataByDay);
    const subtotalKeys = Object.keys(this.dailySubtotals);
    const validKeys = groupedKeys.filter(key => subtotalKeys.includes(key));

    return validKeys.sort((a, b) => {
      if (a === 'No Date') return 1;
      if (b === 'No Date') return -1;
      return moment(a).diff(moment(b));
    });
  }

  // Get refunds for a specific date
  getRefundsForDate(dateKey: string): any[] {
    if (!this.royaltyDataRefunds) return [];
    return this.royaltyDataRefunds.filter(refund => {
      const refundDate = refund.date_paid || 'No Date';
      return refundDate === dateKey;
    });
  }

  override setupPagination(data: any, params: { parameter: string; value: string; }[]) {
    const pageParam = params.find(param => param.parameter === 'page');

    // Check if paginator exists before accessing its properties
    if (this.paginator) {
      this.paginator.length = data?.['count'] ?? 0;
      this.paginator.pageSize = this.pageSize;
      this.paginator.pageIndex = pageParam ? parseInt(pageParam.value, 10) : 0;
    }

    // DO NOT call updateRouterParams here - this prevents duplicate requests
    // The routing has already been handled by pageChanged() or other methods
    LoggerService.log('setupPagination called - skipping route update to prevent duplicates');
  }

  override onPrint() {
    let content = `
      <h2 class="report-title">Royalty Report</h2>
      <p class="report-period">Week ${this.week}, ${this.year}</p>
    `;

    // Group data by day
    const sortedDates = Object.keys(this.groupedDataByDay).sort();

    sortedDates.forEach(dateKey => {
      const rows = this.groupedDataByDay[dateKey];
      const subtotal = this.dailySubtotals[dateKey] || this.getDefaultSubtotal();

      content += `
        <div class="day-section">
          <h4>${this.reportDateService.formatDate(dateKey)}</h4>
          <table>
            <thead>
              <tr>
                <th>Receipt #</th>
                <th>Date Paid</th>
                <th>Student</th>
                <th>Executive</th>
                <th>Instructor</th>
                <th>Sale Code</th>
                <th>Package</th>
                <th>Lessons</th>
                <th>Total</th>
                <th>Cash</th>
                <th>Misc</th>
                <th>Royalty</th>
                <th>Sundry</th>
                <th>Tax</th>
                <th>Receipts</th>
              </tr>
            </thead>
            <tbody>
              ${rows.map(row => `
                <tr>
                  <td>${row.receipt_number || ''}</td>
                  <td>${this.reportDateService.formatDate(row.date_paid)}</td>
                  <td>${row.student_name || ''}</td>
                  <td>${row.field_executive_display || ''}</td>
                  <td>${row.field_instructor_display || ''}</td>
                  <td>${row.sale_code || ''}</td>
                  <td>${row.package_name || ''}</td>
                  <td>${row.lesson_count || 0}</td>
                  <td>${row.total_price || 0}</td>
                  <td>${row.cash || 0}</td>
                  <td>${row.miscellaneous_services || 0}</td>
                  <td>${row.subject_to_royalty || 0}</td>
                  <td>${row.sundry || 0}</td>
                  <td>${row.field_tax_collected || 0}</td>
                  <td>${row.total_receipts || 0}</td>
                </tr>
              `).join('')}
              <tr class="subtotal-row">
                <td colspan="8"><strong>Daily Subtotal</strong></td>
                <td><strong>${subtotal.total_price || 0}</strong></td>
                <td><strong>${subtotal.cash || 0}</strong></td>
                <td><strong>${subtotal.miscellaneous_services || 0}</strong></td>
                <td><strong>${subtotal.subject_to_royalty || 0}</strong></td>
                <td><strong>${subtotal.sundry || 0}</strong></td>
                <td><strong>${this.currencyService.format(this.roundNumber(subtotal.tax || 0))}</strong></td>
                <td><strong>${subtotal.total_receipts || 0}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      `;
    });

    // Add refunds section if exists
    if (this.royaltyDataRefunds && this.royaltyDataRefunds.length > 0) {
      content += `
        <div class="section">
          <h3>Refunds</h3>
          <table>
            <thead>
              <tr>
                <th>Receipt #</th>
                <th>Date</th>
                <th>Student</th>
                <th>Package</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              ${this.royaltyDataRefunds.map(row => `
                <tr>
                  <td>${row.receipt_number || ''}</td>
                  <td>${this.reportDateService.formatDate(row.date_paid)}</td>
                  <td>${row.student_name || ''}</td>
                  <td>${row.package_name || ''}</td>
                  <td>${row.total_price || 0}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `;
    }

    // Add Summary section
    const footerData = this.data?.['#royalty_content_table']?.['staff_data_4_system']?.['#footer']?.[0]?.data;
    if (footerData) {
      const totals = footerData.total || {};
      const refunded = footerData.total_refunded || {};
      const includeRefunded = footerData.total_include_refunded || {};

      content += `
        <div class="section summary-section">
          <h3>Summary</h3>
          <table>
            <thead>
              <tr>
                <th></th>
                <th>Cash</th>
                <th>Misc Services</th>
                <th>Subject to Royalty</th>
                <th>Sundry</th>
                <th>Tax</th>
                <th>Total Receipts</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Totals</strong></td>
                <td>${this.currencyService.format(this.roundNumber(totals.cash || 0))}</td>
                <td>${this.currencyService.format(this.roundNumber(totals.miscellaneous_services || 0))}</td>
                <td>${this.currencyService.format(this.roundNumber(totals.subject_to_royalty || 0))}</td>
                <td>${this.currencyService.format(this.roundNumber(totals.sundry || 0))}</td>
                <td>${this.currencyService.format(this.roundNumber(totals.tax || 0))}</td>
                <td>${this.currencyService.format(this.roundNumber(totals.total_receipts || 0))}</td>
              </tr>
              <tr>
                <td><strong>Deduct Refunds/Credits</strong></td>
                <td>${this.currencyService.format(this.roundNumber(refunded.cash || 0))}</td>
                <td>${this.currencyService.format(this.roundNumber(refunded.miscellaneous_services || 0))}</td>
                <td>${this.currencyService.format(this.roundNumber(refunded.subject_to_royalty || 0))}</td>
                <td>${this.currencyService.format(this.roundNumber(refunded.sundry || 0))}</td>
                <td>${this.currencyService.format(this.roundNumber(refunded.tax || 0))}</td>
                <td>${this.currencyService.format(this.roundNumber(refunded.total_receipts || 0))}</td>
              </tr>
              <tr>
                <td><strong>Total Subject To Percentage</strong></td>
                <td>${this.currencyService.format(this.roundNumber(includeRefunded.cash || 0))}</td>
                <td>${this.currencyService.format(this.roundNumber(includeRefunded.miscellaneous_services || 0))}</td>
                <td>${this.currencyService.format(this.roundNumber(includeRefunded.subject_to_royalty || 0))}</td>
                <td></td>
                <td></td>
                <td></td>
              </tr>
              <tr class="royalty-row">
                <td><strong>Weekly Net Due (${this.royaltyPercentage || 0}%)</strong></td>
                <td>${this.currencyService.format(this.calcRoyaltyPercentage(includeRefunded.cash || 0))}</td>
                <td>${this.currencyService.format(this.calcRoyaltyPercentage(includeRefunded.miscellaneous_services || 0))}</td>
                <td>${this.currencyService.format(this.calcRoyaltyPercentage(includeRefunded.subject_to_royalty || 0))}</td>
                <td></td>
                <td></td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      `;
    }

    const styles = `
      /* Layout and page setup */
      @media print {
        @page {
          size: letter landscape;
          margin: 0.4in;
        }
        body {
          zoom: 1;
          transform: scale(1);
          transform-origin: top left;
          width: 100%;
          padding: 0 !important; /* Override print service padding */
        }
      }
      @page {
        size: letter landscape;
        margin: 0.4in;
      }
      .report-title {
        font-size: 16px;
        margin-bottom: 5px;
      }
      .report-period {
        font-size: 11px;
        margin-bottom: 8px;
      }
      .day-section {
        margin-bottom: 10px;
      }
      .day-section h4 {
        font-size: 12px;
        margin: 5px 0;
        background-color: #f0f0f0;
        padding: 2px 5px;
      }
      table {
        font-size: 10px;
        width: 100%;
      }
      th, td {
        padding: 2px 4px;
        border: 1px solid #ddd;
      }
      .subtotal-row {
        background-color: #f5f5f5;
        font-weight: bold;
      }
      .summary-section {
        margin-top: 15px;
        page-break-inside: avoid;
      }
      .summary-section h3 {
        font-size: 14px;
        margin-bottom: 5px;
        border-bottom: 2px solid #333;
        padding-bottom: 3px;
      }
      .summary-section table {
        font-size: 10px;
      }
      .summary-section th,
      .summary-section td {
        padding: 4px 6px;
      }
      .royalty-row {
        background-color: #e3f2fd;
        font-weight: bold;
      }
    `;

    this._printService.print(content, {
      title: `Royalty Report - Week ${this.week}, ${this.year}`,
      styles: styles,
      showSchoolName: true,
      promptPrint: true
    });
  }

}
